import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAuth } from "../components/AuthProvider.js";
import { ErrorBoundary } from "../components/ErrorBoundary.js";
import { api } from "../lib/api.js";
import { Button } from "../components/ui/button.js";
import { Input } from "../components/ui/input.js";
import { Download, Upload } from "lucide-react";

function GuestsPageWithErrorBoundary() {
  return (
    <ErrorBoundary componentName="GuestsPage">
      <GuestsPage />
    </ErrorBoundary>
  );
}

export const Route = createFileRoute("/guests/")({ component: GuestsPageWithErrorBoundary });

interface Guest {
  id: string;
  name: string;
  email: string;
  title?: string;
  company?: string;
  totalEpisodes: number;
  tags: string[];
  status: string;
}

const statusLabels: Record<string, string> = {
  active: "Active",
  inquiry: "Inquiry",
  archived: "Archived",
};

const statusColors: Record<string, string> = {
  active: "bg-chart-2/10 text-chart-2",
  inquiry: "bg-chart-3/10 text-chart-3",
  archived: "bg-muted text-muted-foreground",
};

function GuestsPage() {
  const { user } = useAuth();
  const [guests, setGuests] = useState<Guest[]>([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{ imported: number; skipped: number; errors: Array<{ row: number; message: string }> } | null>(null);
  const fileInputRef = useState<HTMLInputElement | null>(null)[0];
  const [, setFileInputRef] = useState<HTMLInputElement | null>(null);

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await api.get("/api/guests/export");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `guestflow-guests-${new Date().toISOString().split("T")[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Export failed:", error);
    } finally {
      setExporting(false);
    }
  };

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImporting(true);
    setImportResult(null);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await api.post("/api/guests/import", formData);
      const result = await response.json();

      if (result.success) {
        setImportResult(result.results);
        // Refresh the guests list
        const refreshUrl = statusFilter === "all" 
          ? `/api/guests?q=${encodeURIComponent(search)}` 
          : `/api/guests?q=${encodeURIComponent(search)}&status=${statusFilter}`;
        const refreshResponse = await api.get(refreshUrl);
        const refreshData = await refreshResponse.json();
        setGuests(refreshData.guests);
      }
    } catch (error) {
      console.error("Import failed:", error);
    } finally {
      setImporting(false);
      // Reset file input
      e.target.value = "";
    }
  };

  useEffect(() => {
    if (!user) return;
    const url = statusFilter === "all" 
      ? `/api/guests?q=${encodeURIComponent(search)}` 
      : `/api/guests?q=${encodeURIComponent(search)}&status=${statusFilter}`;
    api.get(url)
      .then(r => r.json())
      .then(d => { setGuests(d.guests); setLoading(false); });
  }, [search, statusFilter, user]);

  if (!user) return <div>Loading...</div>;

  // Show inquiry count badge
  const inquiryCount = guests.filter(g => g.status === "inquiry").length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-3xl font-bold">Guests</h1>
          {inquiryCount > 0 && (
            <span className="px-2 py-1 text-xs font-medium bg-chart-3/10 text-chart-3 rounded-full">
              {inquiryCount} new inquiry{inquiryCount > 1 ? "ies" : "y"}
            </span>
          )}
        </div>
        <Link to="/guests/new"><Button>+ Add Guest</Button></Link>
        <div className="flex gap-2">
          <input
            type="file"
            accept=".csv"
            onChange={handleImport}
            className="hidden"
            id="csv-import"
          />
          <Button 
            variant="outline" 
            onClick={() => document.getElementById('csv-import')?.click()}
            disabled={importing}
          >
            <Upload className="h-4 w-4 mr-2" />
            {importing ? "Importing..." : "Import CSV"}
          </Button>
          <Button variant="outline" onClick={handleExport} disabled={exporting || guests.length === 0}>
            <Download className="h-4 w-4 mr-2" />
            {exporting ? "Exporting..." : "Export CSV"}
          </Button>
        </div>
        </div>
        {importResult && (
          <div className={`p-4 rounded-lg ${importResult.errors.length > 0 ? 'bg-yellow-50 border border-yellow-200' : 'bg-green-50 border border-green-200'}`}>
            <p className="font-medium">
              Import complete: {importResult.imported} guests imported
              {importResult.skipped > 0 && `, ${importResult.skipped} skipped`}
            </p>
            {importResult.errors.length > 0 && (
              <ul className="mt-2 text-sm text-yellow-800">
                {importResult.errors.slice(0, 5).map((err, i) => (
                  <li key={i}>Row {err.row}: {err.message}</li>
                ))}
                {importResult.errors.length > 5 && (
                  <li>...and {importResult.errors.length - 5} more errors</li>
                )}
              </ul>
            )}
            <button 
              onClick={() => setImportResult(null)} 
              className="mt-2 text-sm text-muted-foreground hover:text-foreground"
            >
              Dismiss
            </button>
          </div>
        )}
      <div className="flex flex-col sm:flex-row gap-4">
        <Input 
          type="text" 
          placeholder="Search guests..." 
          value={search} 
          onChange={e => setSearch(e.target.value)} 
          className="flex-1"
        />
        <div className="flex gap-2">
          <button 
            onClick={() => setStatusFilter("all")}
            className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
              statusFilter === "all" 
                ? "bg-primary text-primary-foreground" 
                : "border hover:bg-muted"
            }`}
          >
            All
          </button>
          <button 
            onClick={() => setStatusFilter("active")}
            className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
              statusFilter === "active" 
                ? "bg-primary text-primary-foreground" 
                : "border hover:bg-muted"
            }`}
          >
            Active
          </button>
          <button 
            onClick={() => setStatusFilter("inquiry")}
            className={`px-3 py-2 rounded-md text-sm font-medium transition-colors relative ${
              statusFilter === "inquiry" 
                ? "bg-primary text-primary-foreground" 
                : "border hover:bg-muted"
            }`}
          >
            Inquiry
            {inquiryCount > 0 && statusFilter !== "inquiry" && (
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-chart-3 text-white text-[10px] rounded-full flex items-center justify-center">
                {inquiryCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-muted">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium">Name</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Title</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Status</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Episodes</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Tags</th>
              </tr>
            </thead>
            <tbody>
              {guests.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                    {statusFilter === "inquiry" ? (
                      <>No pending inquiries. <Link to="/booking" className="text-primary hover:underline" target="_blank">Share your booking form</Link> to get started.</>
                    ) : (
                      <>No guests yet. <Link to="/guests/new" className="text-primary hover:underline">Add your first guest</Link>.</>
                    )}
                  </td>
                </tr>
              ) : guests.map(guest => (
                <tr key={guest.id} className="border-t hover:bg-muted/50">
                  <td className="px-4 py-3">
                    <Link to="/guests/$guestId" params={{ guestId: guest.id }} className="block">
                      <div className="font-medium hover:text-primary">{guest.name}</div>
                      <div className="text-sm text-muted-foreground">{guest.email}</div>
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-sm">{guest.title && guest.company ? `${guest.title} at ${guest.company}` : guest.title || guest.company || "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 text-xs rounded-full ${statusColors[guest.status] || "bg-muted text-muted-foreground"}`}>
                      {statusLabels[guest.status] || guest.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm">{guest.totalEpisodes}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1 flex-wrap">
                      {guest.tags?.map(tag => <span key={tag} className="px-2 py-1 text-xs bg-secondary rounded-lg">{tag}</span>)}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
